import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import styles from './Goals.module.scss';
import { useAppSelector } from '../../../../redux/hooks';
import {
  useUpdateGoalsDataMutation,
  useGetGoalsFullDataQuery,
  useCreateGoalMutation,
} from '../../../../redux/services/goalsAPI';
import ABOBackgroundLoader from '../../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader';
import ErrorInPageScreen from '../../../common/ErrorInPageScreen/ErrorInPageScreen';
import { isValidResponse } from '../../../../utils/validateAPIResponse';
import { useProfileParams } from '../../../../utils/common';
import { ReactComponent as EmptyGoalsLightImg } from '../../../../assets/images/LightTheme/no_connection_light.svg';
import { ReactComponent as EmptyGoalsDarkImg } from '../../../../assets/images/DarkTheme/no_connection_dark.svg';
import TextButton from '../../../common/TextButton/TextButton';
import ViewGoals from '../ViewGoals/ViewGoals';
import Modal from '../../../common/Modal/Modal';
import AddGoalModal from './AddGoalModal/AddGoalModal';
import { disableEnableScroll } from '../../../../utils/disableEnableScroll';
import Icon from '../../../common/Icon/Icon';

type GoalsProp = {
  selectedHistoryData: HistoryItem | null;
  showDetailsPage: boolean;
  setShowDetailsPage: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedHistoryData: React.Dispatch<React.SetStateAction<HistoryItem | null>>;
};
const Goals: React.FC<GoalsProp> = ({
  selectedHistoryData,
  setSelectedHistoryData,
  showDetailsPage,
  setShowDetailsPage,
}) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const [goalsData, setGoalsData] = useState<GoalData[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);
  const [isAPIError, setIsAPIError] = useState(false);
  const [showViewGoals, setShowViewGoals] = useState(false);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [modalOpen, setModalOpen] = useState(false);

  // API Queries
  const {
    data: goalsResponseData,
    isFetching: isLoadingGetGoals,
    isError: isFetchGoalsError,
  } = useGetGoalsFullDataQuery(
    { affiliateCode, aboNumber },
    { refetchOnMountOrArgChange: true, skip: isAPIError },
  );

  const [createGoals, { isLoading: isLoadingCreateGoals }] =
    useCreateGoalMutation();
  const [
    updateGoalsData,
    { isLoading: isLoadingUpdateGoals, isError: isErrorInSave },
  ] = useUpdateGoalsDataMutation();

  // Handlers
  const handleAPIError = () => {
    setIsAPIError(true);
    setGoalsData([]);
  };

  const handleTryAgain = () => {
    setIsAPIError(false);
  };

  const handleClickAddGoal = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleBackToList = () => {
    setSelectedHistoryData(null);
    setShowDetailsPage((prev) => !prev);
  };

  const handleCreateGoalSave = (goal: any) => {
    const goalPayload = {
      ...(goal?.aboGoalId && { aboGoalId: goal?.aboGoalId }),
      title: goal?.goalTitle,
      note: goal?.note,
      timeframeType: goal.timeFrame,
      kpiCodes: goal.kpiCodes.map((metric: any) => metric.code) || [],
      outcomeType: goal?.outcome,
      outcomeDetail: {
        incomeType: goal?.incomeType,
        incomeValue: +goal?.monthlyIncome,
        awardCode: goal?.awardType?.code,
        other:
          goal?.outcome === "9" || goal?.outcome === 9
            ? "other description"
            : "",
      },
    };

    createGoals({
      affiliateCode,
      aboNumber,
      payload: goalPayload,
    }).then((resp: any) => {
      const goalRestData = resp?.data?.body;
      if (!isEmpty(resp?.data?.body)) {
        setGoalsData((prevGoals) => {
          const exists = prevGoals.some(
            (g) => g?.aboGoalId === goalRestData?.aboGoalId,
          );
          if (exists) {
            return prevGoals.map((goalItem) =>
              goalItem?.aboGoalId === goalRestData?.aboGoalId
                ? { ...goalItem, ...goalRestData }
                : goalItem,
            );
          }
          return [...prevGoals, goalRestData];
        });
      }
    });

    setShowViewGoals(true);
    setModalOpen(false);
  };

  const handleHistoryGoalSave = (updatedGoal: any) => {
    setSelectedHistoryData((prev) => {
      if (!prev) return prev;
      if (prev.aboGoalId !== updatedGoal.aboGoalId) return prev;
      return { ...prev, ...updatedGoal } as HistoryItem;
    });
  };

  const handleGoalSave = (updatedGoal: any) => {
    updateGoalsData({
      affiliateCode,
      aboNumber,
      payload: updatedGoal,
    }).then((resp: any) => {
      const updatedGoalRespData = resp?.data?.body;
      if (!isEmpty(resp?.data?.body) && resp?.data?.statusCode === 200) {
        setGoalsData((prevGoals: any) => {
          const exists = prevGoals?.some(
            (goal: any) => goal?.aboGoalId === updatedGoalRespData?.aboGoalId,
          );
          if (exists) {
            return prevGoals.map((goalItem: any) =>
              goalItem?.aboGoalId === updatedGoalRespData?.aboGoalId
                ? { ...goalItem, ...updatedGoalRespData }
                : goalItem,
            );
          }
          return prevGoals;
        });
      }
    });
  };

  // Effects
  useEffect(() => {
    if (isFetchGoalsError || isErrorInSave) handleAPIError();
  }, [isFetchGoalsError, isErrorInSave]);

  useEffect(() => {
    if (!isEmpty(goalsResponseData)) {
      if (isValidResponse(goalsResponseData?.statusCode)) {
        setGoalsData(
          !isEmpty(goalsResponseData?.body) ? goalsResponseData?.body : [],
        );
      } else {
        handleAPIError();
      }
    }
  }, [goalsResponseData, isLoadingGetGoals]);

  useEffect(() => {
    disableEnableScroll(modalOpen);
  }, [modalOpen]);

  // Derived state
  const isLoading =
    isLoadingGetGoals || isLoadingUpdateGoals || isLoadingCreateGoals;
  const showEmptyState =
    !showViewGoals && isEmpty(goalsData) && isEmpty(selectedHistoryData);
  const hasHistoryData = !isEmpty(selectedHistoryData);
  const EmptyGoalsImage =
    currentTheme === "light" ? EmptyGoalsLightImg : EmptyGoalsDarkImg;
  const iconColor = currentTheme === "light" ? "#2C2C2C" : "#D8D8D9";
  const themeColor = currentTheme === "light" ? "#000" : "#fff";
  const wrapperClassName = `goals_wrapper ${styles.goals_wrapper} ${isAPIError ? styles.full_width : ""}`;

  const sharedGoalProps = {
    setShowDetailsPage,
    showDetailsPage,
    selectedGoalId,
    setSelectedGoalId,
    selectedHistoryData,
  };

  // Render helpers
  const renderEmptyState = () => (
    <div className={`goals_section ${styles.goals_section}`}>
      <div className={`empty_goals ${styles.empty_goals}`}>
        <EmptyGoalsImage height={150} />
        <div className={`empty_goals_text ${styles.empty_goals_text}`}>
          <span>{t("noGoalsMsg")}</span>
          <TextButton
            label={t("addGoal")}
            onClick={handleClickAddGoal}
            data-testid="add_goal_testID"
          />
        </div>
      </div>
    </div>
  );

  const renderGoalsList = () => {
    if (showEmptyState) {
      return renderEmptyState();
    }

    if (hasHistoryData) {
      return (
        <ViewGoals
          key={selectedHistoryData?.aboGoalId}
          goalData={selectedHistoryData}
          onGoalSave={handleHistoryGoalSave}
          {...sharedGoalProps}
        />
      );
    }

    const visibleGoals = !showDetailsPage
      ? goalsData
      : goalsData?.filter((goals) => goals?.aboGoalId === selectedGoalId);

    if (isEmpty(visibleGoals)) {
      return renderEmptyState();
    }

    return visibleGoals.map((data: GoalData) => (
      <ViewGoals
        key={data?.aboGoalId}
        goalData={data}
        onGoalSave={handleGoalSave}
        {...sharedGoalProps}
      />
    ));
  };

  // Early returns for loading and error states
  if (isLoading) {
    return (
      <div className={wrapperClassName}>
        <ABOBackgroundLoader num={1} heading={t("goals")} />
      </div>
    );
  }

  if (isAPIError) {
    return (
      <div className={wrapperClassName}>
        <div className={styles.heading_area}>
          <span
            className={styles.heading}
            data-testid="goals_bg_heading_testID"
          >
            {t("goals")}
          </span>
        </div>
        <ErrorInPageScreen handleClickTryAgain={handleTryAgain} />
      </div>
    );
  }

  // Main render
  return (
    <div className={wrapperClassName}>
      {/* Header */}
      {showDetailsPage ? (
        <span
          className={styles.details_page_heaading}
          data-testid="goals_details_heading_testID"
          onClick={handleBackToList}
        >
          <Icon
            name="chevron_left_icon"
            size={13}
            className={styles.circle_icon}
            color={iconColor}
          />
          <span className={styles.details_heading_text}>
            {t("back_to_list")}
          </span>
        </span>
      ) : (
        <div className={styles.heading_area}>
          <span
            className={styles.heading}
            data-testid="goals_bg_heading_testID"
          >
            {t("goals")}
          </span>
          <span className={styles.right_section}>
            {!isEmpty(goalsData) && (
              <TextButton
                label={t("addGoal")}
                onClick={handleClickAddGoal}
                data-testid="add_goal_testID"
                isIcon={true}
                iconName="plus_big"
                iconSize={16}
                themeColor={themeColor}
              />
            )}
          </span>
        </div>
      )}

      {/* Add Goal Modal */}
      <Modal showDrawer={modalOpen} onClose={handleModalClose}>
        <AddGoalModal
          onClose={handleModalClose}
          onSave={handleCreateGoalSave}
        />
      </Modal>

      {/* Goals List */}
      {renderGoalsList()}
    </div>
  );
};

export default Goals;
